/**
 * Browser port of tools/migration-v2/src/state.ts (the run memory), plus a pretend HRMS
 * database so the simulator can show what the tool writes, what reset forgets and what it
 * leaves behind. Everything lives in this browser's localStorage; nothing reaches the API.
 */
import { describeFilters, type SelectionFilters } from "./selection.js";
import { defaultSourceSettings, type SimEmployee, type SourceSettings } from "./source.js";

export const STATE_FILE = "tools/migration-v2/state/migration-state.json";
const STORAGE_KEY = "hrms.migrationSim.v1";

export interface ModuleSummary { module: string; read: number; written: number; skipped: number; dryRun: boolean; at: string }
export interface Progress { migrated: number; total: number; percent: number }

export interface RunRecord {
  id:          string;
  startedAt:   string;
  finishedAt?: string | undefined;
  dryRun:      boolean;
  outcome:     "running" | "done" | "done-with-errors" | "crashed";
  filters:     SelectionFilters;
  filterText:  string;
  matched:     number;
  bayIds:      number[];
  loaded:      number;
  failed:      Array<{ bayId: number; code: string; error: string }>;
  progressAfter?: Progress | undefined;
  modules?:    ModuleSummary[] | undefined;
  verify?:     { at: string; summary: VerifySummary } | undefined;
}

export interface EmployeeRecord { code: string; name: string; status: string; department: string; runId: string; at: string }

export interface MigrationState {
  version:   2;
  createdAt: string;
  source:    { total: number; byStatus: Record<string, number>; byDepartment: Record<string, number>; at: string } | null;
  runs:      RunRecord[];
  employees: Record<string, EmployeeRecord>;
  verifications: Array<{ at: string; scope: string; summary: VerifySummary }>;
}

export interface VerifySummary {
  scope: string;
  employees?: { picked: number; inHrms: number; missing: number[]; withoutLogin: string[] } | undefined;
  employeesChecked: number;
  withNoOldLeaveOrAttendance: number;
  leave: { match: number; mismatch: number; skippedRowsByReason: Record<string, number>; oldUsersNotMigrated: number };
  attendance: { match: number; mismatch: number };
  totals: { oldLeaves: number; newLeaves: number; oldAttendanceDays: number; newAttendanceDays: number };
}

/** What the pretend HRMS database holds, keyed by legacy bay id. */
export interface HrmsDb {
  employees:    Record<string, { code: string; managerSet: boolean }>;
  leaves:       Record<string, number>;
  attendance:   Record<string, number>;
  personal:     Record<string, true>;
  terminations: Record<string, true>;
  compensation: Record<string, true>;
  documents:    Record<string, number>;
  holidays:     number;
}

export interface SimWorld {
  memory:   MigrationState;
  hrms:     HrmsDb;
  archives: Array<{ file: string; state: MigrationState }>;
  files:    Record<string, string>;      // uploaded codes files, by name
  settings: SourceSettings;
  hrReport: string | null;               // the HR report "file" last uploaded, by name
}

export function emptyState(): MigrationState {
  return { version: 2, createdAt: new Date().toISOString(), source: null, runs: [], employees: {}, verifications: [] };
}

export function emptyHrms(): HrmsDb {
  return { employees: {}, leaves: {}, attendance: {}, personal: {}, terminations: {}, compensation: {}, documents: {}, holidays: 0 };
}

export function emptyWorld(): SimWorld {
  return { memory: emptyState(), hrms: emptyHrms(), archives: [], files: {}, settings: { ...defaultSourceSettings }, hrReport: null };
}

export function loadWorld(): SimWorld {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...emptyWorld(), ...(JSON.parse(raw) as Partial<SimWorld>) };
  } catch { /* private window or blocked storage: start fresh */ }
  return emptyWorld();
}

export function saveWorld(world: SimWorld): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(world)); } catch { /* storage full or blocked: keep it in memory */ }
}

export function migratedBayIds(state: MigrationState): Set<number> {
  return new Set(Object.keys(state.employees).map(Number));
}

export function updateSource(state: MigrationState, all: SimEmployee[]): void {
  const byStatus: Record<string, number> = {};
  const byDepartment: Record<string, number> = {};
  for (const m of all) {
    byStatus[m.status] = (byStatus[m.status] ?? 0) + 1;
    byDepartment[m.departmentName] = (byDepartment[m.departmentName] ?? 0) + 1;
  }
  state.source = { total: all.length, byStatus, byDepartment, at: new Date().toISOString() };
}

export function progress(state: MigrationState): Progress {
  const total = state.source?.total ?? 0;
  const migrated = Object.keys(state.employees).length;
  return { migrated, total, percent: total ? Math.round((migrated / total) * 1000) / 10 : 0 };
}

function bar(percent: number, width = 30): string {
  const full = Math.round((Math.min(percent, 100) / 100) * width);
  return `[${"█".repeat(full)}${"░".repeat(width - full)}]`;
}

export interface Breakdown { key: string; migrated: number; total: number; percent: number }

export function breakdown(state: MigrationState, by: "status" | "department"): Breakdown[] {
  if (!state.source) return [];
  const totals = by === "status" ? state.source.byStatus : state.source.byDepartment;
  const done: Record<string, number> = {};
  for (const e of Object.values(state.employees)) {
    const k = by === "status" ? e.status : e.department;
    done[k] = (done[k] ?? 0) + 1;
  }
  return Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([key, total]) => {
    const migrated = done[key] ?? 0;
    return { key, migrated, total, percent: total ? Math.round((migrated / total) * 100) : 0 };
  });
}

export function progressLines(state: MigrationState, detail = false): string[] {
  const p = progress(state);
  const out = ["", "── Migration progress ────────────────────────────"];
  if (!state.source) {
    out.push("  No run yet: the source total is counted by the first migrate:team run.");
    return out;
  }
  out.push(`  ${bar(p.percent)} ${p.percent}%  (${p.migrated} of ${p.total} employees)`);
  const real = state.runs.filter((r) => !r.dryRun);
  out.push(`  runs: ${real.length} real, ${state.runs.length - real.length} dry  ·  memory: ${STATE_FILE}`);
  if (!detail) return out;
  for (const by of ["status", "department"] as const) {
    out.push("", `  by ${by}:`);
    for (const b of breakdown(state, by)) {
      out.push(`    ${b.key.slice(0, 26).padEnd(26)} ${String(b.migrated).padStart(5)} / ${String(b.total).padEnd(5)} ${bar(b.percent, 15)} ${b.percent}%`);
    }
  }
  return out;
}

export function startRun(state: MigrationState, f: SelectionFilters, matched: number, picked: SimEmployee[], dryRun: boolean): RunRecord {
  const run: RunRecord = {
    id: `run-${String(state.runs.length + 1).padStart(3, "0")}`,
    startedAt: new Date().toISOString(),
    dryRun,
    outcome: "running",
    filters: f,
    filterText: describeFilters(f),
    matched,
    bayIds: picked.map((m) => m.legacyBayUserId),
    loaded: 0,
    failed: [],
  };
  state.runs.push(run);
  return run;
}

// A dry run records what it would have picked but remembers no one as migrated.
export function finishRun(state: MigrationState, run: RunRecord, picked: SimEmployee[], errors: Array<{ id: number; error: string }>): void {
  const failed = new Map(errors.map((e) => [e.id, e.error]));
  const at = new Date().toISOString();
  for (const m of picked) {
    const error = failed.get(m.legacyBayUserId);
    if (error !== undefined) { run.failed.push({ bayId: m.legacyBayUserId, code: m.employeeCode, error }); continue; }
    run.loaded++;
    if (run.dryRun) continue;
    state.employees[String(m.legacyBayUserId)] = {
      code: m.employeeCode, name: `${m.firstName} ${m.lastName}`.trim(), status: m.status,
      department: m.departmentName, runId: run.id, at,
    };
  }
  run.finishedAt = at;
  run.outcome = run.failed.length ? "done-with-errors" : "done";
  run.progressAfter = progress(state);
}

export function findRun(state: MigrationState, ref: string): RunRecord {
  const run = ref === "last" ? state.runs[state.runs.length - 1] : state.runs.find((r) => r.id === ref);
  if (!run) {
    const known = state.runs.map((r) => r.id).join(", ") || "none yet";
    throw new Error(`No migration run "${ref}" in ${STATE_FILE} (runs: ${known})`);
  }
  return run;
}

export function historyLines(state: MigrationState): string[] {
  const out = ["", "── Run history ───────────────────────────────────"];
  if (!state.runs.length) { out.push("  no runs yet"); return out; }
  for (const r of state.runs) {
    const p = r.progressAfter ? `${r.progressAfter.percent}%` : "-";
    out.push(`  ${r.id}  ${r.startedAt.slice(0, 16).replace("T", " ")}  ${r.dryRun ? "DRY " : "REAL"}  ${r.outcome.padEnd(16)} picked=${String(r.bayIds.length).padEnd(4)} loaded=${String(r.loaded).padEnd(4)} failed=${String(r.failed.length).padEnd(3)} progress=${p}`);
    out.push(`         filters: ${r.filterText}`);
    if (r.modules?.length) out.push(`         modules: ${r.modules.map((m) => `${m.module} ${m.written}/${m.read}`).join(", ")}`);
    if (r.verify) out.push(`         verified ${r.verify.at.slice(0, 16).replace("T", " ")}`);
  }
  return out;
}

/** Archives the memory and starts empty. The pretend HRMS data is not touched, as in the tool. */
export function resetState(world: SimWorld): string | null {
  const m = world.memory;
  if (!m.runs.length && !Object.keys(m.employees).length && !m.source) return null;
  const file = `tools/migration-v2/state/archive/migration-state-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  world.archives.push({ file, state: m });
  world.memory = emptyState();
  return file;
}
